// ─────────────────────────────────────────────────────────────────────────────
// MuteButton — desktop/herexy port of the iOS reference
// (mobile/rexy-ios/src/components/home/MuteButton.tsx). Phase 3.4.
//
// Behaviour is 1:1 with iOS: single click → toggleMute, which emits voice:mute /
// voice:unmute; the server echoes voice:mute_state (the source of truth for
// `muted`). NO session teardown — mute keeps the session + keep-alive alive.
//
// Mute = in-call quiet-presence / USER-INPUT mute (1.5 §7, 1.2 §6, Phase 7.10):
// voice:mute stops forwarding the user's mic audio to STT and makes barge-in
// inert (server side, voice_ws.py:312-314). It does NOT stop Rexy speaking (agent
// TTS continues), does NOT suppress escalation, does NOT affect attention/
// tracking, does NOT touch sync. It is NOT escalation suppression.
//
// The mute MECHANIC already exists on desktop (usePTT toggleMute → socket.mute/
// unmute → voice:mute_state echo, exposed via useAppShellLive). This component is
// only the missing ICON BUTTON, conformed to the iOS one.
//
// Design: dimensional per the locked Pixel constraints — a raised card surface
// (drop shadow + 1px top-edge highlight) that presses into a well on click. The
// plain-React/CSS equivalent of the iOS RaisedSurface + PressableSurface
// primitives (desktop has no primitives lib). Same icon assets as iOS
// (Mic.png / mic_muted.png), transparent RGBA, no tint.
//
// Present ONLY during an active session — matching iOS (listening /
// user_speaking / thinking / agent_speaking). Rendered as a SIBLING of the orb
// slot (never a child) so its clicks do not bubble to the orb's PTT onMouseDown.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { useAppShellLive } from './AppShellContext';
import { REXY_CONSOLE } from '../../lib/rexyConsole';
import micIcon from '../../assets/Mic.png';
import micMutedIcon from '../../assets/mic_muted.png';

const SIZE = 64;

export default function MuteButton() {
  const { state, muted, toggleMute } = useAppShellLive();
  const [pressed, setPressed] = useState(false);

  // Active-session-only — identical gate to the iOS MuteButton.
  const active =
    state === 'listening' ||
    state === 'user_speaking' ||
    state === 'thinking' ||
    state === 'agent_speaking';
  if (!active) return null;

  return (
    <button
      type="button"
      onClick={toggleMute}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      aria-label={muted ? 'Unmute microphone' : 'Mute microphone'}
      aria-pressed={muted}
      title={muted ? 'Unmute' : 'Mute'}
      style={{
        width: SIZE,
        height: SIZE,
        borderRadius: SIZE / 2,
        padding: 0,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: REXY_CONSOLE.cardBg,
        border: `1px solid ${REXY_CONSOLE.cardBorder}`,
        // Dimensional: raised at rest (drop shadow + top-edge highlight), pressed
        // into a well on click. Never flat (Pixel 3D constraint).
        boxShadow: pressed
          ? 'inset 0 2px 6px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04)'
          : '0 4px 12px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.08)',
        transform: pressed ? 'translateY(1px) scale(0.97)' : 'none',
        transition: 'box-shadow 120ms ease, transform 120ms ease',
        WebkitUserSelect: 'none',
        userSelect: 'none',
      }}
    >
      <img
        src={muted ? micMutedIcon : micIcon}
        alt=""
        draggable={false}
        style={{
          width: SIZE * 0.5,
          height: SIZE * 0.5,
          objectFit: 'contain',
          background: 'transparent',
          pointerEvents: 'none',
        }}
      />
    </button>
  );
}
